import {
  Stethoscope, Wrench, Home, Scale, UtensilsCrossed, HardHat,
  Building2, Megaphone, Truck, Store, Landmark
} from 'lucide-react';

const industriesData = {
  'dental-medical-offices': {
    icon: Stethoscope,
    title: 'Dental & Medical Offices',
    tab: 'small',
    tagline: 'Automate patient communication so your front desk can focus on care.',
    overview: 'Dental and medical practices lose hours every week to phone calls, appointment reminders, insurance questions, and patient intake. Agentic Atoms builds AI systems that handle these tasks around the clock — reducing no-shows, capturing new patient leads after hours, and keeping your front desk focused on in-office care.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — 24/7 Patient Assistant', desc: 'Answers insurance FAQs, handles new patient inquiries, books appointments, and captures leads from your website — even at 2 AM on a Saturday.' },
      { key: 'receptionist', name: 'AI Receptionist — Handles Calls Automatically', desc: 'Takes incoming calls, schedules appointments, confirms insurance details, and routes urgent calls to staff. Reduces front-desk call volume so your team can focus on patients in the office.' },
      { key: 'automation', name: 'Lead Capture & Follow-Up Automation', desc: "Automatically follows up with new patient inquiries, sends appointment reminders, and re-engages patients who haven't visited in 6+ months." },
    ],
    useCases: [
      'New patient calls after hours — AI captures details and books the first available slot',
      'Insurance eligibility questions answered instantly via chatbot',
      'Automated recall campaigns for patients overdue for cleanings or check-ups',
      'Intake forms sent and collected digitally before the appointment',
    ],
    stats: [
      { value: '40%', label: 'reduction in front-desk call volume' },
      { value: '67%', label: 'of patients prefer online booking' },
      { value: '30%', label: 'decrease in no-show rates with AI reminders' },
    ]
  },
  'auto-repair-shops': {
    icon: Wrench,
    title: 'Auto Repair Shops',
    tab: 'small',
    tagline: 'Never miss a weekend lead or after-hours service request again.',
    overview: 'Auto repair shops lose revenue every weekend and evening when the phone goes to voicemail. Agentic Atoms builds AI systems that capture every lead, provide instant estimates, and follow up automatically — so your bays stay full without chasing every call.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Instant Quote & Booking', desc: 'Answers common repair questions, provides ballpark estimates, and books service appointments directly from your website — day or night.' },
      { key: 'receptionist', name: 'AI Receptionist — After-Hours Call Handler', desc: 'Captures service requests from weekend and evening callers, collects vehicle details, and schedules the next available appointment automatically.' },
      { key: 'automation', name: 'Lead Capture & Follow-Up Automation', desc: "Follows up on open estimates, sends service reminders based on mileage intervals, and re-engages past customers who haven't returned." },
    ],
    useCases: [
      'Saturday night call about a check-engine light — AI captures details and books Monday morning',
      'Customer texts asking for a brake job estimate — chatbot provides a range instantly',
      'Automated follow-up on estimates that were given but never approved',
      'Service reminders sent at 30k, 60k, 90k mile intervals',
    ],
    stats: [
      { value: '35%', label: 'of leads come outside business hours' },
      { value: '6x', label: 'faster response time with AI vs. callbacks' },
      { value: '$4K+', label: 'average recovered revenue per month' },
    ]
  },
  'real-estate-agencies': {
    icon: Home,
    title: 'Real Estate Agencies',
    tab: 'small',
    tagline: 'Qualify leads 24/7 and never let a hot prospect go cold.',
    overview: 'In real estate, speed to lead is everything. Agentic Atoms builds AI systems that qualify buyer and seller leads around the clock, book showings automatically, and keep follow-up sequences running — so your agents spend time closing, not chasing.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Lead Qualifier & Showing Scheduler', desc: 'Engages website visitors, qualifies buyer/seller intent, answers property questions, and books showings — all without agent involvement.' },
      { key: 'receptionist', name: 'AI Receptionist — Inbound Lead Handler', desc: 'Answers calls from listing signs, Zillow leads, and website inquiries. Captures contact info, qualifies the lead, and routes hot prospects to the right agent.' },
      { key: 'automation', name: 'Lead Nurture & Follow-Up Automation', desc: "Automatically sends property matches, market updates, and follow-up messages. Keeps cold leads warm until they're ready to move." },
    ],
    useCases: [
      'Zillow lead comes in at 10 PM — AI qualifies and books a showing for tomorrow',
      'Open house visitors scanned — automated follow-up sequence starts within the hour',
      'Seller lead calls from a "For Sale" sign — AI captures details and alerts the listing agent',
      'Monthly market reports sent automatically to nurture leads',
    ],
    stats: [
      { value: '78%', label: 'of buyers go with the first agent to respond' },
      { value: '5 min', label: 'response window before lead quality drops 80%' },
      { value: '3x', label: 'more showings booked with 24/7 AI' },
    ]
  },
  'law-firms': {
    icon: Scale,
    title: 'Law Firms',
    tab: 'small',
    tagline: 'Screen clients and book consultations without pulling attorneys off billable work.',
    overview: 'Every minute an attorney spends on intake calls is a minute not spent on billable work. Agentic Atoms builds AI systems that screen and qualify potential clients, collect preliminary case details, and schedule consultations.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Client Screening & Intake', desc: 'Asks qualifying questions, determines case type and urgency, collects contact information, and books a consultation — all from your website.' },
      { key: 'receptionist', name: 'AI Receptionist — Call Screening & Routing', desc: 'Handles initial client calls, screens for case fit, collects key details, and routes qualified leads to the appropriate attorney.' },
      { key: 'automation', name: 'Client Follow-Up & Intake Automation', desc: "Sends engagement letters, collects signed documents, follows up with prospects who didn't book, and keeps your pipeline moving." },
    ],
    useCases: [
      'Personal injury lead calls at 8 PM — AI screens the case and books a morning consultation',
      'Website visitor asks about divorce process — chatbot qualifies and captures lead',
      'Automated follow-up on consultation no-shows with rescheduling link',
      'Client intake forms sent and organized before the first meeting',
    ],
    stats: [
      { value: '42%', label: 'of legal inquiries come outside office hours' },
      { value: '10hrs', label: 'saved per week on intake and screening' },
      { value: '60%', label: 'of website visitors leave without contacting' },
    ]
  },
  'restaurants-hospitality': {
    icon: UtensilsCrossed,
    title: 'Restaurants & Hospitality',
    tab: 'small',
    tagline: 'Handle reservations and guest questions without pulling staff off the floor.',
    overview: "Restaurant staff shouldn't be stuck on the phone answering the same questions about hours, menus, and availability. Agentic Atoms builds AI systems that handle reservations, event inquiries, catering quotes, and guest communication.",
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Reservations & Guest Support', desc: 'Handles reservation requests, answers menu and allergen questions, provides event/catering info, and captures special occasion bookings.' },
      { key: 'receptionist', name: 'AI Receptionist — Phone Reservation Handler', desc: 'Takes reservation calls, handles takeout orders, answers hours/location questions, and manages waitlist additions.' },
      { key: 'automation', name: 'Guest Follow-Up & Marketing Automation', desc: 'Sends reservation confirmations, post-visit feedback requests, birthday promotions, and re-engagement campaigns for lapsed guests.' },
    ],
    useCases: [
      'Friday night reservation request — AI books it and sends confirmation instantly',
      'Catering inquiry on Sunday — chatbot collects event details and budget',
      'Automated birthday emails with a complimentary dessert offer',
      'Post-dining feedback triggers a Google review request for 5-star experiences',
    ],
    stats: [
      { value: '25%', label: 'of reservation calls go unanswered at peak' },
      { value: '70%', label: 'of diners prefer booking online or via chat' },
      { value: '2x', label: 'more event inquiries with 24/7 AI response' },
    ]
  },
  'service-contractors': {
    icon: HardHat,
    title: 'Service Contractors',
    tab: 'small',
    tagline: 'Book jobs, send reminders, and follow up on estimates — on autopilot.',
    overview: 'HVAC, plumbing, electrical, and landscaping businesses run on booked jobs. Agentic Atoms builds AI systems that capture leads from every channel, book appointments, follow up on outstanding estimates, and keep your schedule full.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Job Booking & Estimates', desc: 'Captures service requests from your website, provides rough time and cost estimates, and books the next available service window.' },
      { key: 'receptionist', name: 'AI Receptionist — Dispatch & Call Handler', desc: 'Answers service calls, captures job details, checks availability, and books the appointment. Handles emergency routing for urgent calls.' },
      { key: 'automation', name: 'Estimate Follow-Up & Maintenance Automation', desc: 'Follows up on open estimates, sends seasonal maintenance reminders, and re-engages past customers.' },
    ],
    useCases: [
      'Homeowner calls about a leak at 9 PM — AI books an emergency morning slot',
      'Open estimate for a $3,500 HVAC install — automated follow-up converts',
      'Spring AC tune-up reminders sent to all past customers automatically',
      'Google Ads lead on Saturday — AI responds in 30 seconds with a booking link',
    ],
    stats: [
      { value: '50%', label: 'of service calls come outside business hours' },
      { value: '$5K+', label: 'recovered monthly from followed-up estimates' },
      { value: '3x', label: 'faster booking vs. traditional callbacks' },
    ]
  },
  'healthcare-groups': {
    icon: Stethoscope,
    title: 'Healthcare Groups & Multi-Location Clinics',
    tab: 'medium',
    tagline: 'Unify patient communication and operations across every location.',
    overview: 'Multi-location healthcare organizations face compounding complexity — different systems, inconsistent patient experiences, and operational blind spots. Agentic Atoms builds centralized AI systems that standardize communication, streamline scheduling, and give leadership visibility across all locations.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Multi-Location Patient Portal', desc: 'Patients interact with one AI that knows which location is closest, which providers are available, and what services each office offers.' },
      { key: 'receptionist', name: 'AI Receptionist — Centralized Call Center', desc: 'Handles inbound calls for all locations through one system. Routes patients to the right office and schedules across providers.' },
      { key: 'automation', name: 'Cross-Location Analytics & Automation', desc: 'Automated appointment reminders, patient satisfaction surveys, recall campaigns, and operational reporting across all locations.' },
    ],
    useCases: [
      'Patient calls main number — AI identifies the nearest location with availability and books directly',
      'Automated recall campaign runs across all locations with location-specific messaging',
      'Monthly performance dashboard shows metrics per site',
    ],
    stats: [
      { value: '60%', label: 'reduction in per-location admin costs' },
      { value: '35%', label: 'improvement in cross-location scheduling' },
      { value: '4.5x', label: 'faster information retrieval' },
    ]
  },
  'manufacturing-supply-chain': {
    icon: Building2,
    title: 'Manufacturing & Supply Chain',
    tab: 'medium',
    tagline: 'Smarter forecasting, fewer bottlenecks, and full operational visibility.',
    overview: 'Manufacturing operations generate massive amounts of data across procurement, production, quality control, and logistics. Agentic Atoms builds AI systems that turn this data into actionable intelligence.',
    services: [
      { key: 'chatbot', name: 'AI Assistant — Internal Operations Q&A', desc: 'Managers ask about inventory levels, order status, production targets, and supplier timelines — in plain English.' },
      { key: 'receptionist', name: 'AI Communication Hub — Vendor & Client Calls', desc: 'Handles inbound vendor calls, routes customer inquiries, and provides automated order status updates.' },
      { key: 'automation', name: 'Supply Chain & Reporting Automation', desc: 'Automated reorder alerts, demand forecasting reports, quality exception notifications, and supplier performance scorecards.' },
    ],
    useCases: [
      'Inventory dips below threshold — AI triggers reorder and notifies procurement',
      'Manager asks "Show me production vs. target for Q1" — gets a chart in seconds',
      'Automated weekly supplier scorecard sent to procurement team',
      'Customer calls about order status — AI provides real-time tracking',
    ],
    stats: [
      { value: '25%', label: 'reduction in excess inventory' },
      { value: '18%', label: 'improvement in on-time delivery' },
      { value: '8hrs', label: 'saved weekly on manual reporting' },
    ]
  },
  'marketing-digital-agencies': {
    icon: Megaphone,
    title: 'Marketing & Digital Agencies',
    tab: 'medium',
    tagline: 'Serve more clients without growing your team.',
    overview: 'Agencies are scaling machines — but every new client adds reporting, communication, and project management overhead. Agentic Atoms builds AI systems that automate client reporting, streamline content production, and keep campaigns running efficiently.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Client Communication Bot', desc: 'Answers common questions about project status, timelines, and deliverables — reducing "just checking in" emails by 70%.' },
      { key: 'receptionist', name: 'AI Receptionist — New Business Intake', desc: 'Handles inbound calls from potential clients, qualifies the opportunity, and books a discovery call with the right account manager.' },
      { key: 'automation', name: 'Reporting & Workflow Automation', desc: 'Automated weekly/monthly client reports. Campaign performance alerts, content calendar reminders, and project milestone notifications.' },
    ],
    useCases: [
      'Client asks "How did our ads perform?" — AI generates the report in 30 seconds',
      'New lead calls — AI qualifies budget and scope, books a sales call',
      'Monday morning campaign summary for all active clients',
      'Content calendar reminders sent to the right team members',
    ],
    stats: [
      { value: '70%', label: 'reduction in status-check emails' },
      { value: '12hrs', label: 'saved weekly on client reporting' },
      { value: '2x', label: 'more clients managed per team member' },
    ]
  },
  'logistics-transportation': {
    icon: Truck,
    title: 'Logistics & Transportation',
    tab: 'medium',
    tagline: 'Optimize routes, automate dispatch, and keep customers informed.',
    overview: 'Logistics companies live and die by efficiency. Agentic Atoms builds AI systems that optimize routing, automate dispatch communications, provide real-time tracking, and give management visibility into fleet performance.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Customer Tracking & Support', desc: 'Customers check shipment status, ETAs, and delivery windows — eliminating "Where\'s my delivery?" phone calls.' },
      { key: 'receptionist', name: 'AI Receptionist — Dispatch & Coordination', desc: 'Handles shipping requests, provides quotes, coordinates pickups, and routes urgent calls to the right dispatcher.' },
      { key: 'automation', name: 'Route Optimization & Alerting', desc: 'Automated delivery notifications, ETA updates, driver scheduling, maintenance reminders, and weekly fleet performance reports.' },
    ],
    useCases: [
      'Customer calls about delivery ETA — AI provides real-time tracking instantly',
      'Maintenance alert at 50k miles — service automatically scheduled',
      'Route optimization runs overnight — morning dispatch has efficient routes ready',
      'Customer notified when delivery is 30 minutes away',
    ],
    stats: [
      { value: '15%', label: 'fuel cost reduction with AI routing' },
      { value: '80%', label: 'fewer "where is my shipment" calls' },
      { value: '22%', label: 'improvement in on-time delivery' },
    ]
  },
  'retail-chains-franchises': {
    icon: Store,
    title: 'Retail Chains & Franchise Operations',
    tab: 'medium',
    tagline: 'Consistent operations and customer experience across every location.',
    overview: 'Multi-location retail and franchise operations need consistency. Agentic Atoms builds AI systems that standardize customer communication, automate inventory management, and provide HQ with real-time performance visibility.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Customer Service & Product Finder', desc: 'Helps shoppers find products, check store availability, and get answers. Consistent brand experience across web and social.' },
      { key: 'receptionist', name: 'AI Receptionist — Store Call Handler', desc: 'Answers store calls, provides hours and directions, checks product availability, and routes complex inquiries to the right manager.' },
      { key: 'automation', name: 'Inventory & Marketing Automation', desc: 'Automated reorder alerts per location, promotional campaigns across stores, loyalty communications, and weekly performance dashboards.' },
    ],
    useCases: [
      'Customer calls Store #7 about product availability — AI checks and responds',
      'New SOP published at HQ — automatically available at all 30 locations',
      'Holiday promotion deployed to all store websites simultaneously',
      'Weekly dashboard comparing revenue and inventory across all stores',
    ],
    stats: [
      { value: '40%', label: 'reduction in HQ-to-store communication lag' },
      { value: '28%', label: 'decrease in out-of-stock incidents' },
      { value: '3x', label: 'faster onboarding of new franchise locations' },
    ]
  },
  'financial-services-insurance': {
    icon: Landmark,
    title: 'Financial Services & Insurance',
    tab: 'medium',
    tagline: 'Faster processing, smarter compliance, and better client communication.',
    overview: 'Financial services firms handle sensitive data, complex regulations, and high client expectations. Agentic Atoms builds AI systems that accelerate claims processing, automate compliance checks, and streamline client onboarding.',
    services: [
      { key: 'chatbot', name: 'AI Chatbot — Client Support & Onboarding', desc: 'Guides new clients through onboarding, answers policy questions, provides account info, and routes complex inquiries to the right advisor.' },
      { key: 'receptionist', name: 'AI Receptionist — Client Call Management', desc: 'Screens calls, routes to the right advisor, handles routine policy questions, and schedules review appointments.' },
      { key: 'automation', name: 'Claims Processing & Compliance Automation', desc: 'Automated document collection for claims, compliance checklist verification, renewal reminders, and regulatory reporting.' },
    ],
    useCases: [
      'Client calls about a claim — AI collects details, checks policy, routes to claims team',
      'New client onboarding — AI sends forms, collects signatures, verifies identity',
      'Annual policy review reminders sent with one-click scheduling',
    ],
    stats: [
      { value: '50%', label: 'faster claims processing' },
      { value: '35%', label: 'reduction in compliance prep time' },
      { value: '90%', label: 'of routine queries handled without advisor' },
    ]
  },
};

export const getIndustryBySlug = (slug) => industriesData[slug] || null;

export const getAllIndustries = () => Object.entries(industriesData).map(([slug, data]) => ({ slug, ...data }));

export const getSmallBusinessIndustries = () =>
  Object.entries(industriesData).filter(([, d]) => d.tab === 'small').map(([slug, data]) => ({ slug, ...data }));

export const getMediumBusinessIndustries = () =>
  Object.entries(industriesData).filter(([, d]) => d.tab === 'medium').map(([slug, data]) => ({ slug, ...data }));

export default industriesData;
